import React from 'react';
import { Route, Routes } from 'react-router-dom';
import Home from '@containers/Home';
import LogOut from '@components/LogOut/LogOut';
import { OKTAErrorContainer } from '@containers/Error.Container';
import { RequiredAuth } from './SecureRoute';
import LoginCallback from '@components/LoginCallback';
import FullAppContainer from '@containers/FullAppContainer';
import { HomeContextProvider } from './context/HomeContext';
import { useQueryClient } from '@tanstack/react-query';
import { fullappLoader } from '@containers/fullAppV2/components/Loader';
import { ErrorPage } from '@containers/fullAppV2/components/Error';
import DocumentView from '@components/DocumentsView/DocumentView';
import { EscrowAnalysisContainer } from '@components_v2/UI/organisms/EscrowAnalysis';
import { ReSkinnedNewLoanInProgressContainer } from '@components_v2/UI/organisms/ReSkinnedNewLoanInProgressContainer';
import { AuthenticationLoader } from '@components_v2/UI/atoms/loading';
import DisasterAssistance from '@components_v2/UI/organisms/DisasterAssistance';
import ImpactedProperty from '@components_v2/UI/organisms/ImpactedProperty';
import { CashOutForm } from '@components_v2/UI/organisms/CashOutForm';
import { RefiForm } from '@components_v2/UI/organisms/RefiForm';
import { PurchaseForm } from '@components_v2/UI/organisms/PurchaseForm';
import { withHeaderFooter, withUIProvider } from '@components_v2/HOCs';
import IdleTimerContainer from '@components_v2/UI/molecule/IdleTimerContainer/IdleTimerContainer';
import { useSelector } from 'react-redux';
import { selectEsignPopUpState, selectParent } from '@store/selectors/lip.selector';
import FullScreenModal from '@components_v2/UI/molecule/FullScreenModal/FullScreenModal';
import { RootState } from '@store';
import { selectFeatureFlag } from '@store/reducers/config.reducer';
import { MigratedLipV2 } from '@components_v2/UI/organisms/MigratedLipV2';
import { useHomeConfig } from '@hooks/useHomeConfig';
import { MessageCenterContainer } from '@components_v2/UI/organisms/MessageCenter';
import { ErrorProps } from '@components_v2/data';
import AccountCare from '@components_v2/UI/organisms/AccountCare';

const HomeWrapper = withUIProvider(Home);
const Loading = () => {
    return <h3 id="loading-icon">Loading...</h3>;
};

const Something = () => <h3 id="loading-icon">Route Not Found</h3>;

const DashboardError = withHeaderFooter(ErrorPage, {
    ...ErrorProps,
    showDropDownOptions: false,
    showMenuOptions: true,
    showFooterAds: false
});

const OktaWrapper = React.memo(() => {
    const queryClient = useQueryClient();
    const parent = useSelector(selectParent);
    const esignPopUpState: string = useSelector(selectEsignPopUpState);
    const isLipRefreshEnabled = useSelector((state: RootState) => selectFeatureFlag(state, 'lipRefresh'));
    const acpRefreshEnabled = useSelector((state: RootState) => selectFeatureFlag(state, 'acpRefresh'));
    const { status: homeStatus } = useHomeConfig();
    if (homeStatus === 'loading') return <AuthenticationLoader />;
    return (
        <React.Fragment>
            <IdleTimerContainer />
            <Routes>
                <Route path="/callback" element={<LoginCallback homePath="/" loadingElement={<AuthenticationLoader />} />} />
                <Route path="/logout" element={<LogOut />} />
                <Route path="/error" element={<DashboardError />} />
                {acpRefreshEnabled ? (
                    <Route path="/" element={<RequiredAuth />}>
                        <Route index element={<HomeContextProvider />} errorElement={<OKTAErrorContainer />} />
                        <Route path=":loanNumber" element={<HomeContextProvider />} />
                        <Route
                            path="new-loan/:loanNumber"
                            element={isLipRefreshEnabled ? <ReSkinnedNewLoanInProgressContainer /> : <MigratedLipV2 />}
                        />
                        <Route path="disaster-assistance" element={<DisasterAssistance />} />
                        <Route path="escrow-analysis/:loanNumber" element={<EscrowAnalysisContainer />} />
                        <Route path="natural-disaster" element={<ImpactedProperty />} />
                        <Route path="pay-now/:loanNumber" element={<AccountCare careType={'oneTimePayment'} />} />
                        <Route path="auto-pay/:loanNumber" element={<AccountCare careType={'recurringPayments'} />} />
                        <Route path="pay-history/:loanNumber" element={<AccountCare careType={'paymentHistory'} />} />
                        <Route path="loan-details/:loanNumber" element={<AccountCare />} />
                        <Route path="statements/:loanNumber" element={<AccountCare careType={'statements'} />} />
                        <Route path="co/:loanNumber" element={<CashOutForm />} />
                        <Route path="sl/:loanNumber" element={<RefiForm />} />
                        <Route path="rt/:loanNumber" element={<RefiForm />} />
                        <Route path="pu/:loanNumber" element={<PurchaseForm />} />
                        <Route
                            path="fullapp/:loanNumber"
                            element={<FullAppContainer />}
                            loader={() => fullappLoader(queryClient)}
                            errorElement={<DashboardError />}
                        />
                        <Route path="mc" element={<MessageCenterContainer />} errorElement={<OKTAErrorContainer />} />

                        {esignPopUpState === 'opened' || (parent && parent.showModal) ? (
                            <Route path="e-sign" element={<FullScreenModal {...parent} />} errorElement={<OKTAErrorContainer />} />
                        ) : null}
                    </Route>
                ) : (
                    <Route path="/" element={<RequiredAuth />}>
                        <Route index element={<HomeWrapper />} errorElement={<OKTAErrorContainer />} />
                    </Route>
                )}
                <Route path="/themeRefresh" element={<RequiredAuth />}>
                    <Route index element={<HomeContextProvider />} errorElement={<OKTAErrorContainer />} />
                    <Route path=":loanNumber" element={<HomeContextProvider />} />
                    <Route path="new-loan/:loanNumber" element={<ReSkinnedNewLoanInProgressContainer />} />
                    <Route path="disaster-assistance" element={<DisasterAssistance />} />
                    <Route path="escrow-analysis/:loanNumber" element={<EscrowAnalysisContainer />} />
                    <Route path="natural-disaster" element={<ImpactedProperty />} />
                    <Route path="co/:loanNumber" element={<CashOutForm />} />
                    <Route path="sl/:loanNumber" element={<RefiForm />} />
                    <Route path="rt/:loanNumber" element={<RefiForm />} />
                    <Route path="pu/:loanNumber" element={<PurchaseForm />} />
                    <Route
                        path="fullapp/:loanNumber"
                        element={<FullAppContainer />}
                        loader={() => fullappLoader(queryClient)}
                        errorElement={<ErrorPage />}
                    />
                    <Route path="mc" element={<MessageCenterContainer />} errorElement={<OKTAErrorContainer />} />

                    {esignPopUpState === 'opened' || (parent && parent.showModal) ? (
                        <Route path="e-sign" element={<FullScreenModal {...parent} />} errorElement={<OKTAErrorContainer />} />
                    ) : null}
                </Route>
                <Route path="*" Component={Something} />
                <Route path="/document" element={<DocumentView />} />
            </Routes>
        </React.Fragment>
    );
});

OktaWrapper.displayName = 'OktaWrapper';
export default OktaWrapper;
